<script setup>
import { ref, computed } from 'vue';
import { Chrome } from '@lk77/vue3-color';
import { OnClickOutside } from '@vueuse/components';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'FFFFFF',
  },
  // Optional: provide a background color to check contrast against
  contrastWith: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const isPickerOpen = ref(false);

const toggleColorPicker = () => {
  isPickerOpen.value = !isPickerOpen.value;
};

const closePicker = () => {
  isPickerOpen.value = false;
};

// Strip leading # for the text input display.
// When the value is the CSS keyword "transparent", show an empty field
// so the user sees only the placeholder (same UX as a cleared field).
const hexInputValue = computed(() => {
  if (!props.modelValue || props.modelValue === 'transparent') return '';
  return props.modelValue.replace(/^#/, '');
});

const updateFromPicker = e => {
  emit('update:modelValue', e.hex);
};

const updateFromInput = e => {
  const raw = e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
  if (raw.length === 0) {
    emit('update:modelValue', 'transparent');
  } else if (raw.length === 6) {
    emit('update:modelValue', `#${raw}`);
  }
};

// ── WCAG contrast helpers ──────────────────────────────────────────────────

function hexToRgb(hex) {
  const clean = hex.replace(/^#/, '');
  if (clean.length !== 6) return null;
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
}

function relativeLuminance({ r, g, b }) {
  const channel = v => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrastRatio(hex1, hex2) {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  if (!rgb1 || !rgb2) return null;
  const l1 = relativeLuminance(rgb1);
  const l2 = relativeLuminance(rgb2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

const contrastInfo = computed(() => {
  if (!props.contrastWith || !props.modelValue) return null;
  const ratio = contrastRatio(props.modelValue, props.contrastWith);
  if (ratio === null) return null;

  let level;
  let passing;
  if (ratio >= 7) {
    level = 'AAA';
    passing = true;
  } else if (ratio >= 4.5) {
    level = 'AA';
    passing = true;
  } else if (ratio >= 3) {
    level = 'AA+'; // passes only for large text
    passing = false;
  } else {
    level = 'Fail';
    passing = false;
  }

  return { ratio: ratio.toFixed(1), level, passing };
});
</script>

<template>
  <div class="relative">
    <label v-if="label" class="block mb-1 text-sm font-medium text-n-slate-12">
      {{ label }}
    </label>
    <OnClickOutside @trigger="closePicker">
      <!-- Trigger row -->
      <div
        class="flex items-center h-9 rounded-lg border border-n-weak overflow-visible focus-within:ring-1 focus-within:ring-woot-500 focus-within:border-woot-500 transition-colors"
      >
        <!-- Color chip -->
        <button
          type="button"
          class="flex items-center justify-center w-10 h-full border-r border-n-weak flex-shrink-0 hover:bg-n-alpha-2 transition-colors rounded-l-lg"
          @click="toggleColorPicker"
        >
          <!-- Transparent indicator -->
          <span
            v-if="!modelValue || modelValue === 'transparent'"
            class="w-5 h-5 rounded-sm border border-black/10 block overflow-hidden relative bg-white"
          >
            <svg
              viewBox="0 0 20 20"
              class="absolute inset-0 w-full h-full"
              xmlns="http://www.w3.org/2000/svg"
            >
              <line
                x1="0"
                y1="20"
                x2="20"
                y2="0"
                stroke="#e53e3e"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </span>
          <!-- Color swatch -->
          <span
            v-else
            class="w-5 h-5 rounded-sm border border-black/10 block"
            :style="{ backgroundColor: modelValue }"
          />
        </button>

        <!-- # prefix + hex input -->
        <div class="flex items-center flex-1 px-2 gap-0.5 min-w-0">
          <span
            class="text-n-slate-10 text-sm font-mono leading-none select-none"
          >
            #
          </span>
          <input
            type="text"
            maxlength="6"
            :value="hexInputValue"
            :placeholder="placeholder"
            class="reset-base flex-1 bg-transparent text-n-slate-12 text-sm font-mono outline-none min-w-0 py-0 placeholder:text-n-slate-7"
            @input="updateFromInput"
          />
        </div>

        <!-- Contrast indicator -->
        <div
          v-if="contrastInfo"
          v-tooltip.top="
            contrastInfo.passing
              ? `Easy to read (contrast ${contrastInfo.ratio}:1)`
              : `Hard to read — try a darker or lighter color (contrast ${contrastInfo.ratio}:1)`
          "
          class="flex-shrink-0 flex items-center justify-center w-8 h-full border-l border-n-weak cursor-default"
        >
          <span
            class="w-4 h-4"
            :class="
              contrastInfo.passing
                ? 'i-lucide-eye text-green-500'
                : 'i-lucide-eye-off text-red-500'
            "
          />
        </div>
      </div>

      <!-- Chrome picker popover -->
      <Chrome
        v-if="isPickerOpen"
        disable-alpha
        :model-value="modelValue"
        class="colorpicker--chrome"
        @update:model-value="updateFromPicker"
      />
    </OnClickOutside>
  </div>
</template>

<style scoped lang="scss">
.colorpicker--chrome.vc-chrome {
  @apply shadow-lg absolute bg-n-background z-[9999] border border-n-weak dark:border-n-weak rounded-[8px] top-full mt-1 left-0;

  :deep() {
    .vc-chrome-saturation-wrap {
      @apply rounded-t-[7px];

      .vc-saturation {
        @apply rounded-t-[8px];
      }
    }

    .vc-chrome-body {
      @apply rounded-b-[7px] bg-n-alpha-3;

      .vc-chrome-toggle-btn {
        .vc-chrome-toggle-icon svg {
          @apply [&>path]:fill-n-slate-10 dark:[&>path]:fill-n-slate-10 left-3 relative;
        }
        .vc-chrome-toggle-icon-highlight {
          @apply bg-n-background;
        }
      }
    }

    input,
    .vc-input__input {
      @apply bg-n-background text-n-slate-12 rounded-md shadow-none;
    }

    .vc-input__label {
      @apply text-n-slate-11 dark:text-n-slate-11;
    }
  }
}
</style>
